import { useMemo, useState } from "react";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { hasInvalidRentingFeeRows } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/editable-renting-fees-table";
import {
  useQpmCatalogItemsListQuery,
  useQpmCatalogItemSetAdditionalsMutation,
} from "@/services/backend/qpm/catalog/api/catalog-api";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import type { CatalogItemRes } from "@/services/backend/qpm/catalog/types/catalog";
import type {
  MonitorAdditionalDTO,
  MonitorAdditionalLibraryItemDTO,
} from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { normalizeMonitorAdditionalConfig } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { AdditionalOverviewTable } from "@/routes/base-data/processes/qpm/_components/additional-overview-table";
import {
  MonitorAdditionalEditForm,
  rentingFeesToRows,
} from "@/routes/base-data/processes/qpm/_components/monitor-additional-edit-form";
import { MonitorAdditionalImageSlot } from "@/routes/base-data/processes/qpm/_components/monitor-additional-image-slot";

type CatalogAdditionalDraft = MonitorAdditionalLibraryItemDTO;

export function useCatalogAdditionalLibraryItems({
  catalogId,
}: {
  catalogId: string;
}) {
  const query = useQpmCatalogItemsListQuery({
    catalogId,
    includedOnly: false,
    limit: 1000,
    offset: 0,
  });

  const items = useMemo(
    () => aggregateCatalogAdditionals(query.data?.items ?? []),
    [query.data?.items],
  );

  return { ...query, catalogAdditionalItems: items };
}

export function CatalogMonitorAdditionalsLibraryTable({
  catalogId,
  monitorType,
}: {
  catalogId: string;
  monitorType: MonitorType;
}) {
  const { data, catalogAdditionalItems, isLoading, error } =
    useCatalogAdditionalLibraryItems({ catalogId });
  const [setItemAdditionals, setItemAdditionalsState] =
    useQpmCatalogItemSetAdditionalsMutation();
  const [editing, setEditing] = useState<CatalogAdditionalDraft | null>(null);

  const persistCatalogAdditional = async (
    additional: CatalogAdditionalDraft,
  ) => {
    const catalogItems = data?.items ?? [];
    const affectedItems = catalogItems.filter((item) =>
      itemContainsAdditional(item, additional),
    );
    await Promise.all(
      affectedItems.map((item) =>
        setItemAdditionals({
          catalogItemId: item.catalogItemId,
          additionals: (item.snapshot.additionals ?? []).map((current) =>
            isSameAdditional(current, additional)
              ? {
                  id: additional.id,
                  name: additional.name,
                  description: additional.description ?? "",
                  basePrice: additional.basePrice ?? 0,
                  rentingFees:
                    additional.monitorType === MonitorType.IoT
                      ? (additional.rentingFees ?? {})
                      : {},
                  config: normalizeMonitorAdditionalConfig(additional.config),
                  createdAt: current.createdAt ?? additional.createdAt,
                  updatedAt: additional.updatedAt,
                }
              : current,
          ),
        }).unwrap(),
      ),
    );
  };

  const removeCatalogAdditional = async (
    additional: CatalogAdditionalDraft,
  ) => {
    const catalogItems = data?.items ?? [];
    const affectedItems = catalogItems.filter((item) =>
      itemContainsAdditional(item, additional),
    );
    await Promise.all(
      affectedItems.map((item) =>
        setItemAdditionals({
          catalogItemId: item.catalogItemId,
          additionals: (item.snapshot.additionals ?? []).filter(
            (current) => !isSameAdditional(current, additional),
          ),
        }).unwrap(),
      ),
    );
    toast.success(t("Material-Zusatz entfernt"));
  };

  const rows = catalogAdditionalItems
    .filter((item) => item.monitorType === monitorType)
    .map((item) => ({
      id: item.id,
      name: item.name,
      monitorType: item.monitorType,
      description: item.description,
      basePrice: item.basePrice ?? 0,
      updatedAt: item.updatedAt,
      rentingFees: rentingFeesToRows(item.rentingFees ?? {}),
      disabled: isLoading || setItemAdditionalsState.isLoading,
      onEdit: () => setEditing(item),
      onDelete: () => removeCatalogAdditional(item),
    }));

  const saveEdit = async () => {
    if (!editing) {
      return;
    }
    const draft = {
      ...editing,
      name: editing.name.trim(),
      description: editing.description?.trim() ?? "",
      updatedAt: new Date().toISOString(),
    };
    if (!draft.name) {
      toast.error(t("Bitte vergeben Sie einen Namen."));
      return;
    }
    if (
      draft.monitorType === MonitorType.IoT &&
      hasInvalidRentingFeeRows(rentingFeesToRows(draft.rentingFees ?? {}))
    ) {
      toast.error(
        t(
          "Bitte prüfen Sie die Mietpreise. Laufzeiten dürfen nicht doppelt vorkommen.",
        ),
      );
      return;
    }
    await persistCatalogAdditional(draft);
    setEditing(null);
    toast.success(t("Material-Zusatz gespeichert"));
  };

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold">{t("Material-Zusätze")}</h3>
        <p className="text-muted-foreground text-sm">
          {t(
            "Diese Material-Zusätze stammen aus den Monitor-Snapshots dieses Katalogs und ändern nur diesen Katalog.",
          )}
        </p>
      </div>
      {error ? (
        <div className="text-destructive text-sm">
          {t("Material-Zusätze konnten nicht geladen werden.")}
        </div>
      ) : null}
      <AdditionalOverviewTable rows={rows} monitorType={monitorType} />
      <CatalogAdditionalDialog
        additional={editing}
        imageMode={
          editing
            ? getFirstCatalogAdditionalImageMode(data?.items ?? [], editing)
            : undefined
        }
        isSaving={setItemAdditionalsState.isLoading}
        onChange={(patch) =>
          setEditing((current) =>
            current
              ? { ...current, ...patch, updatedAt: new Date().toISOString() }
              : current,
          )
        }
        onCancel={() => setEditing(null)}
        onConfirm={saveEdit}
      />
    </section>
  );
}

function CatalogAdditionalDialog({
  additional,
  imageMode,
  isSaving,
  onChange,
  onCancel,
  onConfirm,
}: {
  additional: CatalogAdditionalDraft | null;
  imageMode?: { kind: "catalog"; catalogItemId: string };
  isSaving: boolean;
  onChange: (patch: Partial<CatalogAdditionalDraft>) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open={additional !== null}
      onOpenChange={(open) => !open && onCancel()}
    >
      <DialogContent className="max-h-[92vh] w-[min(96vw,1280px)] max-w-none overflow-hidden p-0 sm:max-w-[1280px]">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>{t("Material-Zusatz bearbeiten")}</DialogTitle>
          <DialogDescription>
            {t(
              "Änderungen werden in allen Monitor-Snapshots dieses Katalogs übernommen, die diesen Zusatz verwenden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[calc(92vh-10rem)] overflow-y-auto px-6 py-4">
          {additional ? (
            <MonitorAdditionalEditForm
              additional={additional}
              monitorType={additional.monitorType}
              disabled={isSaving}
              onChange={onChange}
              imageSlot={
                <MonitorAdditionalImageSlot
                  additional={additional}
                  mode={imageMode}
                  disabled={isSaving}
                />
              }
            />
          ) : null}
        </div>
        <DialogFooter className="border-t px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
          >
            {t("Abbrechen")}
          </Button>
          <Button type="button" onClick={onConfirm} disabled={isSaving}>
            {t("Speichern")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function getFirstCatalogAdditionalImageMode(
  items: CatalogItemRes[],
  additional: CatalogAdditionalDraft,
): { kind: "catalog"; catalogItemId: string } | undefined {
  const item = items.find((currentItem) =>
    itemContainsAdditional(currentItem, additional),
  );
  return item
    ? { kind: "catalog", catalogItemId: item.catalogItemId }
    : undefined;
}

function aggregateCatalogAdditionals(
  catalogItems: CatalogItemRes[],
): CatalogAdditionalDraft[] {
  const byKey = new Map<string, CatalogAdditionalDraft>();
  for (const item of catalogItems) {
    for (const additional of item.snapshot.additionals ?? []) {
      const key = additionalKey(additional);
      if (!key || byKey.has(key)) {
        continue;
      }
      byKey.set(key, {
        ...additional,
        monitorType: item.snapshot.type,
        config: normalizeMonitorAdditionalConfig(additional.config),
        sourceMonitorId: item.baseMonitorId,
        sourceMonitorName: item.snapshot.name,
      });
    }
  }
  return [...byKey.values()];
}

function itemContainsAdditional(
  item: CatalogItemRes,
  additional: CatalogAdditionalDraft,
): boolean {
  return (item.snapshot.additionals ?? []).some((current) =>
    isSameAdditional(current, additional),
  );
}

function isSameAdditional(
  current: MonitorAdditionalDTO,
  additional: CatalogAdditionalDraft,
): boolean {
  return additionalKey(current) === additionalKey(additional);
}

function additionalKey(additional: { id?: string; name?: string }) {
  if (additional.id) {
    return `id:${additional.id}`;
  }
  const name = (additional.name ?? "").trim().toLocaleLowerCase();
  return name ? `name:${name}` : "";
}
